<template>
  <div class="d-inline-block">
    <b-skeleton-wrapper :loading="isLoadInProgress">
      <template #loading>
        <div class="product-box">
          <div class="item-image-holder d-flex justify-content-center align-items-center">
            <b-skeleton-img no-aspect animation="fade" class="w-100 h-100" />
          </div>
          <div class="d-flex justify-content-between w-100 p-lg-2">
            <div class="item w-100">
              <b-skeleton class="w-50" />
              <div class="item-name">
                <b-skeleton class="w-100 mt-3" />
              </div>
              <b-skeleton class="w-25" />
              <div class="d-flex justify-content-between align-items-center mt-2">
                <b-skeleton class="w-25" />
                <b-skeleton class="w-25" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="product-box d-flex flex-column justify-content-between h-100">
        <div v-if="productToShow !== null" class="product-box d-flex flex-column justify-content-between h-100">
          <div class="d-flex flex-column">
            <div class="item-image-holder d-flex justify-content-center align-items-center">
              <a
                v-if="productToShow.anchorTarget === '_blank'"
                :href="productToShow.url"
                :title="productNameWithBrandAndPackaging"
                :target="productToShow.anchorTarget"
                itemprop="image"
                itemscope
                itemtype="https://schema.org/ImageObject"
                @click="sendDataLayerItemSelected(productToShow)"
              >
                <b-img-lazy
                  :src="productToShow.imageSrc"
                  :alt="productNameWithBrandAndPackaging"
                  itemprop="url"
                />
                <meta itemprop="url" :content="productToShow.imageSrc">
                <meta itemprop="width" content="600">
                <meta itemprop="height" content="600">
              </a>
              <router-link
                v-else
                :to="productToShow.url"
                :title="productNameWithBrandAndPackaging"
                :target="productToShow.anchorTarget"
                itemprop="image"
                itemscope
                itemtype="https://schema.org/ImageObject"
                @click="sendDataLayerItemSelected(productToShow)"
              >
                <b-img-lazy
                  :src="productToShow.imageSrc"
                  :alt="productNameWithBrandAndPackaging"
                  itemprop="url"
                />
                <meta itemprop="url" :content="productToShow.imageSrc">
                <meta itemprop="width" content="600">
                <meta itemprop="height" content="600">
              </router-link>
            </div>
            <div class="d-flex justify-content-between w-100 p-lg-2">
              <div class="item w-100">
                <a
                  v-if="productToShow.anchorTarget === '_blank'"
                  :href="productToShow.brandUrl"
                  :target="productToShow.anchorTarget"
                  @click="sendDataLayerItemSelected(productToShow)"
                >
                  <div v-text="productToShow.brandName" class="brand-name my-2" />
                </a>
                <router-link
                  v-else
                  :to="productToShow.brandUrl"
                  :target="productToShow.anchorTarget"
                  @click="sendDataLayerItemSelected(productToShow)"
                >
                  <div v-text="productToShow.brandName" class="brand-name my-2" />
                </router-link>
                <a
                  v-if="productToShow.anchorTarget === '_blank'"
                  :href="productToShow.url"
                  :title="productToShow.productNameWithBrandAndPackaging"
                  :target="productToShow.anchorTarget"
                  @click="sendDataLayerItemSelected(productToShow)"
                >
                  <div class="item-name">
                    {{ productToShow.name }}
                    <template v-if="productToShow.packaging !== ''">({{ productToShow.packaging }})</template>
                  </div>
                </a>
                <router-link
                  v-else
                  :to="productToShow.url"
                  :title="productToShow.productNameWithBrandAndPackaging"
                  :target="productToShow.anchorTarget"
                  @click="sendDataLayerItemSelected(productToShow)"
                >
                  <div class="item-name">
                    {{ productToShow.name }}
                    <template v-if="productToShow.packaging !== ''">({{ productToShow.packaging }})</template>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
          <div class="d-flex justify-content-between w-100 p-lg-2">
            <div class="item w-100">
              <a
                v-if="productToShow.anchorTarget === '_blank'"
                :href="productToShow.url"
                :title="productToShow.productNameWithBrandAndPackaging"
                :target="productToShow.anchorTarget"
                @click="sendDataLayerItemSelected(productToShow)"
              >
                <rating-stars
                  :rating="productToShow.rating"
                  :review-count="productToShow.reviewCount"
                  :is-review-count-visible="false"
                  class="mt-2"
                />
              </a>
              <router-link
                v-else
                :to="productToShow.url"
                :title="productToShow.productNameWithBrandAndPackaging"
                :target="productToShow.anchorTarget"
                @click="sendDataLayerItemSelected(productToShow)"
              >
                <rating-stars
                  :rating="productToShow.rating"
                  :review-count="productToShow.reviewCount"
                  :is-review-count-visible="false"
                  class="mt-2"
                />
              </router-link>
              <div class="badge-container d-flex flex-wrap">
                <div class="badge-block d-flex flex-wrap">
                  <div
                    v-if="productToShow.isDiscounted"
                    class="badge badge-discount"
                  >
                    -{{ productToShow.discountPercent }}%
                  </div>
                </div>
                <div class="badge-block d-flex flex-wrap">
                  <div
                    v-if="productToShow.isNewItem"
                    class="badge badge-newitem"
                    v-t="'NEW_PRODUCT_FLAG'"
                  />
                </div>
              </div>
              <div class="price old">
                <span v-if="productToShow.isDiscounted">
                  {{ productToShow.originalPriceFormatted }}
                  <span
                    v-if="productToShow.originalPriceSecondaryFormatted !== null"
                    class="ml-1"
                  >
                    ({{ productToShow.originalPriceSecondaryFormatted }})
                  </span>
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div
                    class="price"
                    :class="{
                      'discounted': productToShow.isDiscounted,
                    }"
                  >
                    {{ productToShow.priceFormatted }}
                    <div v-if="productToShow.priceSecondaryFormatted !== null">
                      ({{ productToShow.priceSecondaryFormatted }})
                    </div>
                  </div>
                  <div class="unit-price">
                    {{ productToShow.unitPriceFormatted }}
                    <div v-if="productToShow.unitPriceSecondaryFormatted !== null">
                      ({{ productToShow.unitPriceSecondaryFormatted }})
                    </div>
                  </div>
                </div>
                <button
                  v-if="!isCatalogPage && productToShow.showAddToCartBtn"
                  :disabled="!productToShow.orderable || isProductAddToCartInProgress"
                  class="custom-button filled add-to-cart d-block d-lg-flex align-items-end justify-content-center mb-3"
                  :class="{
                    'show-icon': cartButtonOnlyIcon,
                  }"
                  @click="addToCart(productToShow)"
                  :aria-label="$t('term_megrendelem')"
                >
                  <span v-if="!cartButtonOnlyIcon" v-t="'term_megrendelem'" class="d-none d-xl-block" />
                </button>
              </div>
            </div>
          </div>
          <add-to-cart-modal
            v-if="currentProductIdForAddToCartModal !== null"
            :product-id="currentProductIdForAddToCartModal"
            @close="currentProductIdForAddToCartModal = null"
          />
        </div>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Axios from 'axios'
import { NOTY_TYPE } from '../../constant/builderConsts.js'
import { BImgLazy, BSkeleton, BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'

export default {
  name: 'ProductBox',
  components: {
    'add-to-cart-modal': () => import(/* webpackChunkName: "AddToCartModal" */ '../../components/AddToCartModal/AddToCartModal.vue'),
    'rating-stars': () => import(/* webpackChunkName: "RatingStars" */ '../RatingStars/RatingStars.vue'),
    'b-img-lazy': BImgLazy,
    'b-skeleton-wrapper': BSkeletonWrapper,
    'b-skeleton-img': BSkeletonImg,
    'b-skeleton': BSkeleton,
  },
  props: {
    /**
     * adatok lekérése folyamatban van-e (kell-e skeleton loader)
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * termék amit kaptunk
     *
     * @type {{
     *   id: Number,
     *   imageSrc: String,
     *   name: String,
     *   packaging: String,
     *   url: String,
     *   productNameWithBrandAndPackaging: String,
     *   brandName: String,
     *   brandUrl: String,
     *   rating: Number,
     *   price: Number,
     *   priceFormatted: String,
     *   isDiscounted: Boolean,
     *   originalPrice: Number,
     *   originalPriceFormatted: String,
     *   giftPoint: Number,
     *   discountPercent: Number,
     *   orderable: Boolean,
     *   isNewItem: Boolean,
     *   showAddToCartBtn: Boolean,
     *   dataLayerData: Object,
     *   flavor: String,
     *   color: String,
     *   size: String,
     *   other: String,
     *   defaultSize: String,
     *   anchorTarget: String,
     * }}
     */
    product: {
      type: Object,
      default: null,
    },
    /**
     * kosárba gomb csak ikon használat
     */
    cartButtonOnlyIcon: {
      type: Boolean,
      default: false,
    },
    /**
     * termék id amit meg akarunk jeleniteni
     */
    productId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      /**
       * kosárba adás folyamatban van-e, ha igen disabled a gomb
       *
       * @type {boolean}
       */
      isProductAddToCartInProgress: false,
      /**
       * termék kosárhoz adás modalhoz a választott termék id
       */
      currentProductIdForAddToCartModal: null,
      /**
       * megjelnitendő termék
       *
       * @type {{
       *   id: Number,
       *   imageSrc: String,
       *   name: String,
       *   packaging: String,
       *   url: String,
       *   productNameWithBrandAndPackaging: String,
       *   brandName: String,
       *   brandUrl: String,
       *   rating: Number,
       *   price: Number,
       *   priceFormatted: String,
       *   originalPriceSecondaryFormatted: String|null,
       *   priceSecondaryFormatted: String|null,
       *   isDiscounted: Boolean,
       *   originalPrice: Number,
       *   originalPriceFormatted: String,
       *   giftPoint: Number,
       *   discountPercent: Number,
       *   orderable: Boolean,
       *   isNewItem: Boolean,
       *   showAddToCartBtn: Boolean,
       *   dataLayerData: Object,
       *   flavor: String,
       *   color: String,
       *   size: String,
       *   other: String,
       *   defaultSize: String,
       *   anchorTarget: String,
       * }}
       */
      productToShow: null,
      /**
       * termék betöltése folyamatban van-e
       *
       * @type {Boolean}
       */
      isProductLoading: false,
    }
  },
  computed: {
    /**
     * termék gyártó név és kiszerelés
     */
    productNameWithBrandAndPackaging() {
      return this.productToShow.brandName + ' ' + this.productToShow.name + (this.productToShow.packaging === '' ? '' : (' (' + this.productToShow.packaging + ')'))
    },
    ...mapGetters({
      isCatalogPage: 'Site/getIsCatalogPage',
    }),
    /**
     * kívülről termék vagy itt belül id alapján termék betöltése folyamatban van-e
     *
     * @type {Boolean}
     */
    isLoadInProgress() {
      return this.isLoading || this.isProductLoading
    },
  },
  created() {
    if (this.product !== null) {
      this.productToShow = this.product
    } else if (!this.isLoading) {
      this.loadProduct()
    }
  },
  methods: {
    ...mapActions({
      getBasicCheckoutData: 'Cart/getBasicCheckoutData',
      getCartAddressData: 'Cart/getCartAddressData',
      getCartEditData: 'Cart/getCartEditData',
    }),
    /**
     * termék betöltése
     */
    loadProduct() {
      if (this.isProductLoading) {
        return
      }
      this.isProductLoading = true
      Axios.get('/Product/api/getProductDataForProductBox', {
        params: {
          itemId: this.productId,
        },
      })
        .then(({ data }) => {
          this.productToShow = data
        })
        .catch((e) => {
          console.error('loadProduct', e)
          this.$root.$emit('notificationPush', this.$t('sikertelen_muvelet'), NOTY_TYPE.NOTY_ERROR)
        })
        .finally(() => {
          this.isProductLoading = false
        })
    },
    /**
     * termék kosárba helyezése
     *
     * @param item
     */
    addToCart(item) {
      this.currentProductIdForAddToCartModal = item.id
    },
    /**
     * clickelt item datalayerbe küldése
     *
     * @param product
     */
    sendDataLayerItemSelected(product) {
      window.dataLayerSelectItem(product.dataLayerData)
    },
  },
}
</script>

<style lang="scss" scoped>
.product-box {
  width: 100%;
  z-index: 1;

  .item-image-holder {
    max-height: calc(min(200px, 35vw));
    width: 100%;

    img {
      max-height: calc(min(200px, 35vw));
      width: calc(min(200px, 35vw));
      height: 100%;
      object-fit: contain;
      aspect-ratio: 1;
    }

    ::v-deep .b-skeleton-img {
      width: 100%;
      height: 200px;
    }
  }

  .unit-price {
    color: #a0a0a0;
    min-height: 20px;
    font-weight: normal;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
  }

  .price {
    min-height: 24px;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    white-space: nowrap;

    @media screen and (max-width: 768px) {
      font-size: 14px;
    }

    @media screen and (max-width: 385px) {
      font-size: 12px;
    }

    &.discounted {
      color: #d6001c;
    }

    &.old {
      min-height: 16px;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      text-decoration: line-through;
      color: #4F4F4F;

      @media screen and (max-width: 768px) {
        font-size: 12px;
      }
    }
  }

  .brand-name {
    color: #4F4F4F;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    text-transform: uppercase;

    @media screen and (max-width: 768px) {
      font-size: 12px;
    }

    @media screen and (max-width: 385px) {
      font-size: 10px;
    }
  }

  .item-name {
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    color: #111;

    @media screen and (max-width: 768px) {
      font-size: 14px;
    }

    @media screen and (max-width: 385px) {
      font-size: 12px;
    }
  }

  .badge-container {
    position: absolute;
    top: 0;
    left: 0;
    flex-direction: column;
  }

  .badge {
    display: block;
    border-radius: var(--product-table-discount-border-radius);
    position: relative;
    font-weight: 500;
    left: 0;
    top: 0;
    font-size: 14px;
    line-height: 16px;
    z-index: 1;
    text-align: center;
    padding: 5px 5px;
    color: #ffffff;
    margin: 5px;

    @media screen and (max-width: 768px) {
      font-size: 12px;
    }

    &.badge-discount {
      background-color: #D6001C;
    }

    &.badge-bestbuy {
      background-color: var(--product-table-product-info-badge-best-buy-background);
    }

    &.badge-recommended {
      background-color: var(--product-table-product-info-badge-recommended-background);
      color: var(--product-table-product-info-badge-recommended-color);
    }

    &.badge-newitem {
      background-color: var(--product-table-product-info-badge-new-item-background);
      color: var(--product-table-product-info-badge-new-item-color);
    }
  }
}
</style>
